import React from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';

const FEATURES = [
  {
    icon: 'ClipboardList',
    title: 'Track every game',
    body: 'Record scores after the run, or use Live Mode to tap-score in real time. Every game saved to your league history.',
  },
  {
    icon: 'BarChart2',
    title: 'See the real standings',
    body: 'Win percentages, points per game, head-to-head records. The numbers settle every argument.',
  },
  {
    icon: 'Users',
    title: 'Find your best lineup',
    body: 'Discover which duos and trios actually win when they play together. Build your dream team from data.',
  },
];

const LandingPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Top bar */}
      <header className="px-4 md:px-6 lg:px-8 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-2" aria-label="HoopStats">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-primary)' }}>
            <span style={{ fontSize: '18px', lineHeight: 1 }}>🏀</span>
          </div>
          <span className="font-heading font-bold text-lg" style={{ color: 'var(--color-foreground)' }}>
            Hoop<span style={{ color: 'var(--color-primary)' }}>Stats</span>
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="px-4 py-2 rounded-lg font-caption text-sm font-medium transition-colors hover:opacity-80"
            style={{ color: 'var(--color-foreground)' }}
          >
            Sign In
          </Link>
          <Link
            to="/login"
            className="px-4 py-2 rounded-lg font-caption text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            Get Started
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 md:px-6 lg:px-8 py-12 md:py-20 max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6" style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-primary)' }} />
          <span className="font-caption text-xs font-semibold" style={{ color: 'var(--color-primary)' }}>For pickup leagues, weekend hoopers, and rec teams</span>
        </div>
        <h1 className="font-heading font-bold text-4xl md:text-6xl leading-tight mb-5" style={{ color: 'var(--color-foreground)' }}>
          Track pickup basketball.<br />
          <span style={{ color: 'var(--color-primary)' }}>See who plays best together.</span>
        </h1>
        <p className="font-body text-base md:text-lg max-w-2xl mx-auto mb-8" style={{ color: 'var(--color-muted-foreground)' }}>
          Record games, follow standings, and discover the duos and trios that actually win. Built for the people who keep showing up.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/login"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-caption text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            Get Started — It's Free
            <Icon name="ArrowRight" size={16} color="#fff" />
          </Link>
          <Link
            to="/login"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-caption text-sm font-semibold border transition-all hover:bg-[var(--color-muted)]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
          >
            I have an account
          </Link>
        </div>
      </section>

      {/* Chemistry mockup preview */}
      <section className="px-4 md:px-6 lg:px-8 pb-12 md:pb-20 max-w-3xl mx-auto">
        <div
          className="rounded-2xl border overflow-hidden"
          style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}
        >
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2">
              <Icon name="Users" size={16} color="var(--color-primary)" />
              <span className="font-heading font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>Best Chemistry</span>
            </div>
            <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Sample data</span>
          </div>
          <div className="px-5 py-4 space-y-3">
            {[
              { rank: 1, names: 'Marcus & Jordan', record: '8W-1L', pct: '89%' },
              { rank: 2, names: 'Sarah & Aisha', record: '6W-2L', pct: '75%' },
              { rank: 3, names: 'Devon & Tyler', record: '5W-3L', pct: '63%' },
            ].map((row) => (
              <div key={row.rank} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-data text-xs font-bold w-5 text-center flex-shrink-0" style={{ color: row.rank === 1 ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}>
                    {row.rank}
                  </span>
                  <span className="font-heading text-sm font-medium truncate" style={{ color: 'var(--color-foreground)' }}>{row.names}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-data text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{row.record}</span>
                  <span className="font-data text-sm font-bold" style={{ color: 'var(--color-primary)' }}>{row.pct}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-4 md:px-6 lg:px-8 pb-16 md:pb-24 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border p-6"
              style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}>
                <Icon name={f.icon} size={22} color="var(--color-primary)" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2" style={{ color: 'var(--color-foreground)' }}>{f.title}</h3>
              <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--color-muted-foreground)' }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-4 md:px-6 lg:px-8 pb-16 md:pb-24 max-w-3xl mx-auto">
        <div
          className="rounded-2xl p-8 md:p-12 text-center"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          <h2 className="font-heading font-bold text-2xl md:text-3xl mb-3 text-white">
            Get on the same court as your stats.
          </h2>
          <p className="font-body text-sm md:text-base mb-6" style={{ color: 'rgba(255,255,255,0.9)' }}>
            Create your league in under a minute. Invite players with a 6-character code.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-caption text-sm font-semibold transition-all hover:opacity-90"
            style={{ backgroundColor: '#fff', color: 'var(--color-primary)' }}
          >
            Get Started
            <Icon name="ArrowRight" size={16} color="currentColor" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 md:px-6 lg:px-8 py-6 max-w-6xl mx-auto" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span style={{ fontSize: '14px' }}>🏀</span>
            <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
              HoopStats · Built for hoopers
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Privacy</Link>
            <Link to="/terms" className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Terms</Link>
            <Link to="/login" className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
